import React, { useState } from "react";
import { UserMinusIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { Friend, removeFriend } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface FriendListItemProps {
  friend: Friend;
  onFriendRemoved?: (friendId: string) => void;
}

export const FriendListItem: React.FC<FriendListItemProps> = ({
  friend,
  onFriendRemoved,
}) => {
  const { user } = useAuth();
  const [isRemoving, setIsRemoving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<Friend | null>(null);

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (friend.friend_profiles?.avatar_url) {
      return friend.friend_profiles.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  const handleRemoveFriend = () => {
    setFriendToRemove(friend);
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!user?.id || !friendToRemove || isRemoving) return;

    setIsRemoving(true);

    try {
      const result = await removeFriend(user.id, friendToRemove.friend_id);

      if (result.success) {
        toast.success(
          `${friendToRemove.friend_profiles?.name} telah dihapus dari daftar teman`
        );

        if (onFriendRemoved) {
          onFriendRemoved(friendToRemove.friend_id);
        }
      } else {
        toast.error(result.error || "Gagal menghapus teman");
      }
    } catch (error) {
      console.error("Error removing friend:", error);
      toast.error("Terjadi kesalahan saat menghapus teman");
    } finally {
      setIsRemoving(false);
      setShowConfirmDialog(false);
      setFriendToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
    setFriendToRemove(null);
  };

  return (
    <>
      <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
        {/* Avatar */}
        <Avatar className="w-8 h-8 flex-shrink-0">
          <AvatarFallback>
            <img
              className="w-full h-full object-cover"
              alt="User Avatar"
              src={getAvatarUrl()}
            />
          </AvatarFallback>
        </Avatar>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[#373e46] text-sm truncate">
            {friend.friend_profiles?.name || "Unknown User"}
          </h4>
          <p className="text-xs text-gray-500 truncate">
            {friend.friend_profiles?.asal_sekolah}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {friend.friend_profiles?.role}
          </p>
        </div>

        {/* Remove Button */}
        <Button
          onClick={handleRemoveFriend}
          disabled={isRemoving}
          size="sm"
          variant="outline"
          className={`px-2 py-1 rounded-lg font-medium transition-all duration-300 text-xs h-7 flex items-center gap-1 border-red-300 text-red-600 hover:bg-red-50 ${
            isRemoving ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isRemoving ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
          ) : (
            <UserMinusIcon className="w-3 h-3" />
          )}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Hapus Teman"
        message={`Apakah Anda yakin ingin menghapus ${friendToRemove?.friend_profiles?.name} dari daftar teman? Tindakan ini tidak dapat dibatalkan.`}
        confirmText="Hapus"
        cancelText="Batal"
        variant="danger"
      />
    </>
  );
};
